/*
 * Первичная загрузка чатов, переписки и людей.
 *
 * События приходят по соединению, а начальное состояние берётся обычными
 * запросами: так открытие приложения не зависит от того, успело ли
 * установиться соединение.
 */

import type { Chat, Message, User } from '@andresot/protocol';
import type { ChatCreate, ChatPreferencesUpdate } from '@andresot/protocol/chats';
import { request } from './client.js';

export async function fetchChats(): Promise<Chat[]> {
	const response = await request<{ chats: Chat[] }>('/api/chats');
	return response.chats;
}

export async function createManagedChat(input: ChatCreate): Promise<Chat> {
	const response = await request<{ chat: Chat }>('/api/chats', { body: input });
	return response.chat;
}

export async function updateChatPreferences(
	chatId: string,
	input: ChatPreferencesUpdate
): Promise<{
	isPinned: boolean;
	isMuted: boolean;
	isUnread: boolean;
	mutedUntil: number | null;
}> {
	const response = await request<{
		preferences: {
			isPinned: boolean;
			isMuted: boolean;
			isUnread: boolean;
			mutedUntil: number | null;
		};
	}>(`/api/chats/${encodeURIComponent(chatId)}/preferences`, { method: 'PATCH', body: input });
	return response.preferences;
}

/** Стереть переписку у себя; у собеседника его копия остаётся. */
export async function clearChatHistory(chatId: string): Promise<void> {
	await request(`/api/chats/${encodeURIComponent(chatId)}/clear`, { method: 'POST' });
}

/** Убрать чат из своего списка. Вернётся сам, когда придёт новое сообщение. */
export async function deleteChat(chatId: string): Promise<void> {
	await request(`/api/chats/${encodeURIComponent(chatId)}`, { method: 'DELETE' });
}

export async function fetchMessages(
	chatId: string,
	options: { afterSeq?: number; aroundSeq?: number; beforeSeq?: number; limit?: number } = {}
): Promise<Message[]> {
	const query = new URLSearchParams();

	if (options.afterSeq !== undefined) query.set('afterSeq', String(options.afterSeq));
	if (options.aroundSeq !== undefined) query.set('aroundSeq', String(options.aroundSeq));
	if (options.beforeSeq !== undefined) query.set('beforeSeq', String(options.beforeSeq));
	if (options.limit !== undefined) query.set('limit', String(options.limit));

	const suffix = query.size > 0 ? `?${query.toString()}` : '';
	const response = await request<{ messages: Message[] }>(`/api/chats/${chatId}/messages${suffix}`);

	return response.messages;
}

/** Изменить своё сообщение через надёжный REST mutation-путь. */
export async function editMessage(messageId: string, text: string): Promise<Message> {
	const response = await request<{ message: Message }>(
		`/api/messages/${encodeURIComponent(messageId)}`,
		{ method: 'PATCH', body: { text } }
	);
	return response.message;
}

export async function searchMessages(chatId: string, query: string): Promise<Message[]> {
	const params = new URLSearchParams({ q: query });
	const response = await request<{ messages: Message[] }>(
		`/api/chats/${encodeURIComponent(chatId)}/search?${params}`
	);
	return response.messages;
}

export async function fetchPinnedMessages(chatId: string): Promise<Message[]> {
	const response = await request<{ messages: Message[] }>(
		`/api/chats/${encodeURIComponent(chatId)}/pinned`
	);
	return response.messages;
}

export async function fetchUsers(): Promise<User[]> {
	const response = await request<{ users: User[] }>('/api/users');
	return response.users;
}
